import { Tabs } from '@ampol/design-system';
import { useState } from 'react';
import AcknowledgeModal from 'features/cards/modals/acknowledge-modal/acknowledge-modal';
import Label from 'constants/labels';

import './cards-list.scss';
import { useSelector } from 'react-redux';
import { useCheckGetAllAccountsRequest } from 'components/use-get-all-accounts-request/use-get-all-accounts-request';
import PageHeader from 'components/layout/page-header/page-header';
import CardFormModal from 'features/cards/modals/card-form-modal/card-form-modal';
import { AccessVerifier } from 'utilities/access-verifier';
import { userActionTypes } from 'utilities/user-utilities';
import { trackCustomEvent } from 'utilities/app-insights';
import useAddCardModal from 'features/cards/components/new-card-modal/add-card-modal/useAddCardModal';
import useEditCardModal from 'features/cards/components/new-card-modal/edit-card-modal/useEditCardModal';
import { selectCardsListPageContext } from '../../../../selectors/cardsSelectors';
import { useCardActivation } from './hooks/useCardActivation';
import { useCardsListTabs } from './hooks/useCardsListTabs';
import { useCardsListModals } from './hooks/useCardsListModals';
import Env from 'Env';

/**
 * CardsList Component
 *
 * Main cards page orchestrator - handles tabs, modals, card selection, add/edit flows.
 * Refactored to use custom hooks for better maintainability and testability.
 */
const CardsList = ({
  modalType,
  contractNumber,
  accountLevel,
  includeTabs = true,
}: {
  modalType?: string;
  contractNumber: string;
  accountLevel: string;
  includeTabs?: boolean;
}) => {
  useCheckGetAllAccountsRequest();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  const newAddCardModal = useAddCardModal();
  const newEditCardModal = useEditCardModal(
    undefined,
    undefined,
    modalType,
    () => isEvWay4Enabled && setShowNewPathEvAcknowledgeModal(true)
  );

  const [selectedCard, setSelectedCard] = useState<{ cardNumber?: string; parent?: string } | null>(null);
  const [showEvAcknowledgeModal, setShowEvAcknowledgeModal] = useState(false);
  // New path (VITE_ENABLE_NEW_CARD_MODALS) doesn't use showAddEditCard, so it needs
  // its own flag to render AcknowledgeModal independently.
  const [showNewPathEvAcknowledgeModal, setShowNewPathEvAcknowledgeModal] = useState(false);

  // Memoized selector: 17 useSelector calls → 1 (94% reduction)
  const {
    canReadWrite,
    assignedGrandfatherClientNumber,
    assignedAccountNumber,
    cardDetail,
    cardDetailLoading,
    loggedInUser,
    primaryActionButton,
    secondaryActionButton,
    activateCardLoading,
    activateCardLoaded,
    isPreviousOrReplacementCardClicked,
    previousOrReplacementCardNumber,
    pendingTransferCount,
    createdCardNumber,
    showCreatedCardModal,
    isCardNumberAdded,
    isGlobalSearchLoaded,
  } = useSelector(selectCardsListPageContext);

  const cardReadAccess = AccessVerifier(['card'], [userActionTypes.CardsRead]);

  // Custom hook: Card activation logic
  const { handleActivateCardClick, btnInActivate } = useCardActivation({
    selectedCard,
    setSelectedCard,
    activateCardLoading,
    activateCardLoaded,
    canReadWrite,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
  });

  // Custom hook: Modal management
  const {
    detailModal,
    successModal,
    showAddEditCard,
    setShowAddEditCard,
    handleAddCardClick,
    handleEditCardClick,
    handleItemSelected,
  } = useCardsListModals({
    selectedCard,
    setSelectedCard,
    modalType,
    cardDetail,
    cardDetailLoading,
    primaryActionButton,
    secondaryActionButton,
    isPreviousOrReplacementCardClicked,
    previousOrReplacementCardNumber,
    createdCardNumber,
    showCreatedCardModal,
    isGlobalSearchLoaded,
    btnInActivate,
    handleActivateCardClick,
    newAddCardModal,
    newEditCardModal,
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
  });

  // Custom hook: Tab management
  const { CardTabs, defaultTab, handleTabClick, CardsComponent } = useCardsListTabs({
    modalType,
    contractNumber,
    accountLevel,
    pendingTransferCount,
    cardReadAccess,
    isCardNumberAdded,
    handleItemSelected,
  });

  return (
    <div className={'cards-list'}>
      {!modalType && (
        <PageHeader
          title='Cards'
          showButton={canReadWrite}
          buttonLabel={'Add new card'}
          onButtonClick={handleAddCardClick}
        />
      )}
      {isEvWay4Enabled && showNewPathEvAcknowledgeModal && (
        <AcknowledgeModal
          title='Card Reissue'
          content={Label.EV_CHARGING_REISSUE_NOTIFICATION}
          onClose={() => setShowNewPathEvAcknowledgeModal(false)}
        />
      )}
      {showAddEditCard && (
        <>
          {isEvWay4Enabled && showEvAcknowledgeModal && (
            <AcknowledgeModal
              title='Card Reissue'
              content={Label.EV_CHARGING_REISSUE_NOTIFICATION}
              onClose={() => {
                setShowEvAcknowledgeModal(false);
                setShowAddEditCard(false);
              }}
            />
          )}
          <CardFormModal
            onClose={() => {
              trackCustomEvent(
                'Close Edit Card Modal',
                loggedInUser,
                assignedAccountNumber,
                'Closed Edit Card modal.',
                {
                  selectedAccount: selectedCard?.parent,
                  selectedCard: selectedCard?.cardNumber,
                }
              );
              setShowAddEditCard(false);
            }}
            onEvReissueComplete={isEvWay4Enabled ? () => setShowEvAcknowledgeModal(true) : undefined}
            cardNumber={selectedCard?.cardNumber}
            formId={'card-edit-form'}
            onCardStatusChange={() => {}}
          />
        </>
      )}
      {includeTabs ? (
        <Tabs items={CardTabs} currentTab={defaultTab} onTabSelected={handleTabClick} />
      ) : (
        <CardsComponent
          modalType={modalType}
          contractNumber={contractNumber}
          accountLevel={accountLevel}
          onItemSelected={handleItemSelected}
        />
      )}
    </div>
  );
};

export default CardsList;
